import React, { type FC } from 'react';
import { Building2 } from 'lucide-react';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import type { ToolCardProps } from './types';
import { safeGet } from './types';

type JobStatus = 'draft' | 'active' | 'closed';

const STATUS_STYLES: Record<JobStatus, { bg: string; color: string; label: string }> = {
  draft: {
    bg: 'rgba(0,0,0,0.04)',
    color: 'var(--text-secondary)',
    label: 'Draft',
  },
  active: {
    bg: 'rgba(0,0,0,0.06)',
    color: 'var(--text-secondary)',
    label: 'Active',
  },
  closed: {
    bg: 'rgba(0,0,0,0.06)',
    color: 'var(--text-secondary)',
    label: 'Closed',
  },
};

export const JobDetailCard: FC<ToolCardProps> = ({
  result,
  stage,
  toolName,
  approvalId,
}) => {
  const title = safeGet<string>(result, 'title');
  const companyRaw = safeGet<unknown>(result, 'company');
  const company =
    (companyRaw && typeof companyRaw === 'object' ? safeGet<string>(companyRaw, 'name') : null) ||
    (typeof companyRaw === 'string' ? companyRaw : null) ||
    safeGet<string>(result, 'companyName');
  const location = safeGet<string>(result, 'location');
  const type = safeGet<string>(result, 'type');
  const workMode = safeGet<string>(result, 'workMode');
  const level = safeGet<string>(result, 'level');
  const description = safeGet<string>(result, 'description');
  const skills = (safeGet<string[]>(result, 'skills') || []).slice(0, 5);
  const rawStatus = safeGet<string>(result, 'status');
  const status: JobStatus =
    rawStatus === 'active' || rawStatus === 'closed' ? rawStatus : 'draft';
  const statusStyle = STATUS_STYLES[status];

  if (!result || typeof result !== 'object') return null;

  const metaSegments = [type, workMode, level].filter(Boolean);

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Status badge */}
        <div>
          <span
            style={{
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 600,
              color: statusStyle.color,
              background: statusStyle.bg,
              borderRadius: 9999,
              padding: '2px 10px',
              lineHeight: '18px',
            }}
          >
            {statusStyle.label}
          </span>
        </div>

        {/* Job title */}
        <div
          style={{
            fontSize: 17,
            fontWeight: 600,
            color: 'var(--text-primary)',
            lineHeight: '22px',
          }}
        >
          {title || 'Untitled Job'}
        </div>

        {/* Company + Location */}
        {(company || location) && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Building2 size={13} style={{ flexShrink: 0 }} />
            {company && <span>{company}</span>}
            {company && location && <span style={{ opacity: 0.5 }}>&middot;</span>}
            {location && <span>{location}</span>}
          </div>
        )}

        {/* Type / workMode / level */}
        {metaSegments.length > 0 && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {metaSegments.map((seg, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ opacity: 0.5 }}>&middot;</span>}
                <span>{seg}</span>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Skills pills */}
        {skills.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {skills.map((skill, i) => (
              <span
                key={i}
                style={{
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'rgba(0,0,0,0.04)',
                  borderRadius: 9999,
                  padding: '2px 8px',
                  lineHeight: '16px',
                  whiteSpace: 'nowrap',
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        )}

        {/* Description */}
        {description && (
          <div
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
              lineHeight: '20px',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </div>
        )}
      </div>

      {/* Approval banner */}
      {stage === 'pending_approval' && approvalId && (
        <ApprovalBanner
          approvalId={approvalId}
          toolName={toolName}
        />
      )}
    </CardShell>
  );
};
